// The Academy and AI marking are on for everyone: nothing here gates them. What remains of the
// analytics SDK's `academy-path` flag is its payload, the founder's list of paused lessons,
// mirrored synchronously so the path never waits for the SDK or the network.
import { useSyncExternalStore } from "react";
import { ACADEMY_PATH_FLAG_KEY, AcademyPathFlagPayload } from "./contract";
import { isRecord } from "./guards";

/** Survives About's delete: it is configuration, not the learner's content. */
export const FLAGS_STORAGE_KEY = "proef.flags";

function parsePayload(payload: unknown): readonly string[] {
  let candidate = payload;
  if (typeof payload === "string") {
    try {
      candidate = JSON.parse(payload);
    } catch {
      return [];
    }
  }
  const parsed = AcademyPathFlagPayload.safeParse(candidate);
  return parsed.success ? parsed.data.unpublished : [];
}

function readUnpublished(): readonly string[] {
  try {
    const raw = window.localStorage.getItem(FLAGS_STORAGE_KEY);
    if (raw === null) return [];
    const stored: unknown = JSON.parse(raw);
    if (!isRecord(stored)) return [];
    const entry = stored[ACADEMY_PATH_FLAG_KEY];
    return isRecord(entry) ? parsePayload(entry["payload"]) : [];
  } catch {
    return [];
  }
}

let unpublished: ReadonlySet<string> = new Set(
  typeof window === "undefined" ? [] : readUnpublished(),
);
const listeners = new Set<() => void>();

/** Lessons the founder pulled remotely: paused for learners inside them, absent from the path. */
export function getUnpublished(): ReadonlySet<string> {
  return unpublished;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useUnpublished(): ReadonlySet<string> {
  return useSyncExternalStore(subscribe, getUnpublished, getUnpublished);
}

/**
 * What the SDK's payload says now. `undefined` means "not evaluated yet", which must never
 * overwrite an earlier answer (that is how the list survives a start-up with the host unreachable).
 */
export function setUnpublishedPayload(payload: unknown): void {
  if (payload === undefined) return;
  const next = parsePayload(payload);
  const changed = next.length !== unpublished.size || next.some((id) => !unpublished.has(id));
  if (!changed) return;
  unpublished = new Set(next);
  try {
    window.localStorage.setItem(
      FLAGS_STORAGE_KEY,
      JSON.stringify({ [ACADEMY_PATH_FLAG_KEY]: { payload: { unpublished: next } } }),
    );
  } catch {
    // Storage blocked: the answer holds for this session.
  }
  listeners.forEach((listener) => {
    listener();
  });
}

/** Forgets what was heard, as a first start would (tests). */
export function resetFlagsForTests(): void {
  unpublished = new Set();
  try {
    window.localStorage.removeItem(FLAGS_STORAGE_KEY);
  } catch {
    // Nothing stored to forget.
  }
  listeners.forEach((listener) => {
    listener();
  });
}
